import Enum from 'enum';
import moment from 'moment';
import {
    isContactKey,
    isThirdPartyContactKey,
    isChatMessageKey,
    isFailedChatMessageKey,
    isChatSessionKey,
    isExtensionKey,
    isChatUserKey,
    isProfileKey,
    isQueueKey,
    isUserSoundKey,
    isVoicemailKey,
    isEntityKey,
    isConferenceKey,
    isConferenceMemberKey,
    isReferKey,
    isChannelKey,
    isBlockListKey,
    isActiveCallKey,
    isIntegrationDomainKey,
    isIntegrationAccessKey,
    isOperatorKey,
} from 'flow/utils';
import { isContactType } from 'flow/utils/extension-utils';
import { isRingtoneDTO } from './utils/ringtone-utils';
import { isFavoriteKey, isFavoriteDTO } from 'flow/utils/favorite-utils';
import ConferenceStateType from 'flow/types/conference-state-type';

const isMaterialStyles = (materialStyles) => {
    return materialStyles !== null && typeof materialStyles === 'object';
};
const isConferenceState = (conferenceState) => {
    return (
        conferenceState !== null &&
        ConferenceStateType.isDefined(conferenceState)
    );
};

const isMoment = (momentObject) => {
    return moment.isMoment(momentObject);
};

const isMomentDuration = (duration) => {
    return moment.isDuration(duration);
};

const isExtensionDTO = (extensionDTO) => {
    return isExtensionKey(extensionDTO.get('key'));
};

const isQueueDTO = (queueDTO) => {
    return isQueueKey(queueDTO.get('key'));
};

const isSoundDTO = (soundDTO) => {
    return isUserSoundKey(soundDTO.get('key'));
};

export const isChatMessageDTO = (chatMessageDTO) => {
    return (
        isChatMessageKey(chatMessageDTO.get('key')) ||
        isFailedChatMessageKey(chatMessageDTO.get('key'))
    );
};

export const isChatSessionDTO = (chatSessionDTO) => {
    return isChatSessionKey(chatSessionDTO.get('key'));
};

export const isContactDTO = (contactDTO) => {
    return (
        isContactKey(contactDTO.get('key')) ||
        contactDTO.get('type') === 'global'
    );
};

const isThirdPartyContactDTO = (thirdPartyContactDTO) => {
    return isThirdPartyContactKey(thirdPartyContactDTO.get('key'));
};

const isCtiContact = (integrationContact) => {
    return integrationContact.has('contactRoute');
};

const isFlowContact = (flowContact) => {
    return (
        isContactDTO(flowContact) ||
        isThirdPartyContactDTO(flowContact) ||
        isContactType(flowContact.get('type')) ||
        isCtiContact(flowContact)
    );
};

const isProfileDTO = (profileDTO) => {
    return isProfileKey(profileDTO.get('key'));
};

const isNumberDTO = (numberDTO) => {
    return numberDTO.has('type') && numberDTO.has('number');
};

const isMainNumber = (mainNumber) => {
    return mainNumber.has('key') && mainNumber.has('description');
};

const isConferenceDTO = (conferenceDTO) => {
    return isConferenceKey(conferenceDTO.get('key'));
};

const isConferenceMemberDTO = (conferenceMemberDTO) => {
    return isConferenceMemberKey(conferenceMemberDTO.get('key'));
};

const isQueueStatSummaryDTO = (queueStatSummaryDTO) => {
    return queueStatSummaryDTO.has('queueStats');
};

const isChannelStatSummaryDTO = (channelStatSummaryDTO) => {
    return channelStatSummaryDTO.has('channelStats');
};

const isIntegrationDomainDTO = (integrationDomainDTO) => {
    return isIntegrationDomainKey(integrationDomainDTO.get('key'));
};

const isActiveCallDTO = (activeCallDTO) => {
    return isActiveCallKey(activeCallDTO.get('key'));
};

const isParkChannelDTO = (parkChannelDTO) => {
    return parkChannelDTO.has('parkChannelName');
};

const isCustomerDTO = (customerDTO) => {
    return isCustomerKey(customerDTO.get('key'));
};

const isCustomerKey = (key) => {
    return /^customer-[0-9]+$/.test(key);
};

const isReferDTO = (referDTO) => {
    return isReferKey(referDTO.get('key'));
};

const isChannelDTO = (channelDTO) => {
    return isChannelKey(channelDTO.get('key'));
};

const isIntegrationAccessDTO = (integrationAccessDTO) => {
    return isIntegrationAccessKey(integrationAccessDTO.get('key'));
};

const isProductDTO = (productDTO) => {
    return /^product-[0-9]+$/.test(productDTO.get('key'));
};

const isProfileAvailabilityKey = (key) => {
    return key === 'available' || key === 'unavailable';
};

const isCallRecordKey = (key) => {
    return 'call-'.indexOf(key);
};

const isCallRecordDTO = (callRecord) => {
    return isCallRecordKey(callRecord.get('key'));
};

const isMediaDevice = (mediaDevice) => {
    return (
        mediaDevice !== null &&
        typeof mediaDevice === 'object' &&
        mediaDevice.deviceId
    );
};

const isCallWidgetKey = (key) => {
    return 'callwidget-'.indexOf(key);
};

const isCallWidgetDTO = (callWidget) => {
    return isCallWidgetKey(callWidget.get('key'));
};

const isQueueWaveKey = (key) => {
    return 'queueWave-'.indexOf(key);
};

const isQueueWaveDTO = (queueWave) => {
    return isQueueWaveKey(queueWave.get('key'));
};

const isQueueMemberKey = (key) => {
    return 'queueMember-'.indexOf(key);
};

const isQueueMemberDTO = (queueMember) => {
    return isQueueMemberKey(queueMember.get('key'));
};

const isQueueAgentProfileDTO = (queueAgentProfileDTO) => {
    return (
        queueAgentProfileDTO.has('queueLoginList') &&
        queueAgentProfileDTO.has('description') &&
        queueAgentProfileDTO.has('available')
    );
};

const isQueueLoginDTO = (queueLoginDTO) => {
    return (
        queueLoginDTO.has('queueEntityKey') &&
        queueLoginDTO.has('queueDescription') &&
        queueLoginDTO.has('queueNumber') &&
        queueLoginDTO.has('loggedIn') &&
        queueLoginDTO.has('key')
    );
};

const isQueueCallStatisticsDTO = (queueCallStatistics) => {
    return (
        queueCallStatistics.has('callId') &&
        queueCallStatistics.has('source') &&
        queueCallStatistics.has('queueKey') &&
        queueCallStatistics.has('answeringAgentKey')
    );
};

const isOperatorDTO = (operator) => {
    return (
        operator.has('key') &&
        operator.has('customerKey') &&
        // && operator.has('membersCount') If this value is 0 this field wont be set from the backend currently.
        operator.has('name')
    );
};

const isOperatorMember = (operatorMember) => {
    return (
        !!operatorMember &&
        operatorMember.has('DTOSubtype') &&
        operatorMember.has('key')
    );
};

const isWebRtcPeer = (webRtcPeer) => {
    // Functions from https://doc-kurento.readthedocs.io/en/stable/_static/utils-jsdoc/module-kurentoUtils.WebRtcPeer.html
    return (
        webRtcPeer !== null &&
        typeof webRtcPeer === 'object' &&
        typeof webRtcPeer.addIceCandidate === 'function' &&
        typeof webRtcPeer.dispose === 'function' &&
        typeof webRtcPeer.processAnswer === 'function' &&
        typeof webRtcPeer.processOffer === 'function'
    );
};

const isMediaDeviceArray = (deviceArray) => {
    return Array.isArray(deviceArray);
};

const isConferenceParticipant = (participant) => {
    return (
        participant !== null &&
        typeof participant === 'object' &&
        typeof participant.get('kmsAudioMuted') === 'boolean' &&
        typeof participant.get('kmsVideoMuted') === 'boolean' &&
        typeof participant.get('sessionAudioMuted') === 'boolean' &&
        typeof participant.get('screenShareActive') === 'boolean'
    );
};

const isMeetingVideoMember = (videoMember) => {
    return (
        videoMember !== null &&
        typeof videoMember === 'object' &&
        typeof videoMember.get('primaryPeer') === 'object' &&
        typeof videoMember.get('videoActive') === 'boolean' &&
        typeof videoMember.get('screenShareActive') === 'boolean'
    );
};

const isMeetingLocation = (meetingLocation) => {
    return (
        meetingLocation.key === 'LOBBY' ||
        meetingLocation.key === 'ACTIVE_MEETING'
    );
};

/**
 * Create a prop-checker function which closes on the supplied
 * predicate and fulfills the React prop-type signature.
 *
 * The returned function will (like React's PropTypes) enable
 * the .isRequired look to enforce specification of prop-types.
 *
 * @param predicate the predicate to dictate validity of prop type
 * @return a function that will pass validation when the supplied
 *         predicate returns true.
 */

const checkProp = (predicate) => {
    const validate = (required, props, propName, componentName) => {
        if (props[propName]) {
            if (predicate(props[propName])) {
                return null;
            } else {
                return new Error(
                    `Prop check failed for '${propName}' in ${componentName}: '${props[propName]}'`,
                );
            }
        } else {
            if (!required) {
                return null;
            } else {
                return new Error(
                    `Required prop '${propName}' is missing in ${componentName}`,
                );
            }
        }
    };

    const check = validate.bind(null, false);
    check.isRequired = validate.bind(null, true);

    return check;
};

/**
 * This prop-checker closes over a parameter that is an Enum.
 *
 * @param an instance of an Enum
 * @returns a function that will pass validation when the supplied parameter is a member of the above Enum.
 */
const checkEnum = (containingEnum) => {
    if (!(containingEnum instanceof Enum)) {
        throw new Error('Enum not supplied for prop check');
    }
    const enumHasItem = (enumItem) => {
        return containingEnum.isDefined(enumItem);
    };
    return checkProp(enumHasItem);
};

export const PropTypes = {
    activeCallDTO: checkProp(isActiveCallDTO),
    activeCallKey: checkProp(isActiveCallKey),
    blockListKey: checkProp(isBlockListKey),
    callRecordKey: checkProp(isCallRecordKey),
    callWidgetDTO: checkProp(isCallWidgetDTO),
    channelDTO: checkProp(isChannelDTO),
    channelKey: checkProp(isChannelKey),
    channelStatSummaryDTO: checkProp(isChannelStatSummaryDTO),
    chatMessageDTO: checkProp(isChatMessageDTO),
    chatMessageKey: checkProp(isChatMessageKey),
    chatPostDTO: checkProp(isChatMessageDTO),
    chatSessionDTO: checkProp(isChatSessionDTO),
    chatSessionKey: checkProp(isChatSessionKey),
    chatUserKey: checkProp(isChatUserKey),
    conferenceDTO: checkProp(isConferenceDTO),
    conferenceMemberDTO: checkProp(isConferenceMemberDTO),
    conferenceKey: checkProp(isConferenceKey),
    contactDTO: checkProp(isContactDTO),
    contactKey: checkProp(isContactKey),
    customerDTO: checkProp(isCustomerDTO),
    customerKey: checkProp(isCustomerKey),
    entityKey: checkProp(isEntityKey),
    extensionDTO: checkProp(isExtensionDTO),
    extensionKey: checkProp(isExtensionKey),
    favoriteDTO: checkProp(isFavoriteDTO),
    favoriteKey: checkProp(isFavoriteKey),
    flowContact: checkProp(isFlowContact),
    inEnum: checkEnum,
    integrationAccessDTO: checkProp(isIntegrationAccessDTO),
    integrationDomainDTO: checkProp(isIntegrationDomainDTO),
    isMoment: checkProp(isMoment),
    isMomentDuration: checkProp(isMomentDuration),
    callRecordDTO: checkProp(isCallRecordDTO),
    mainNumber: checkProp(isMainNumber),
    materialStyles: checkProp(isMaterialStyles),
    conferenceState: checkProp(isConferenceState),
    numberDTO: checkProp(isNumberDTO),
    parkChannelDTO: checkProp(isParkChannelDTO),
    productDTO: checkProp(isProductDTO),
    profileAvailabilityKey: checkProp(isProfileAvailabilityKey),
    profileDTO: checkProp(isProfileDTO),
    profileKey: checkProp(isProfileKey),
    queueDTO: checkProp(isQueueDTO),
    queueKey: checkProp(isQueueKey),
    queueStatSummaryDTO: checkProp(isQueueStatSummaryDTO),
    referDTO: checkProp(isReferDTO),
    ringtoneDTO: checkProp(isRingtoneDTO),
    soundDTO: checkProp(isSoundDTO),
    thirdPartyContactDTO: checkProp(isThirdPartyContactDTO),
    thirdPartyContactKey: checkProp(isThirdPartyContactKey),
    usersoundKey: checkProp(isUserSoundKey),
    voicemailKey: checkProp(isVoicemailKey),
    mediaDevice: checkProp(isMediaDevice),
    mediaDeviceArray: checkProp(isMediaDeviceArray),
    webRtcPeer: checkProp(isWebRtcPeer),
    conferenceParticipant: checkProp(isConferenceParticipant),
    meetingVideoMember: checkProp(isMeetingVideoMember),
    meetingLocation: checkProp(isMeetingLocation),
    queueWaveDTO: checkProp(isQueueWaveDTO),
    queueMemberDTO: checkProp(isQueueMemberDTO),
    queueCallStatisticsDTO: checkProp(isQueueCallStatisticsDTO),
    operatorKey: checkProp(isOperatorKey),
    operatorMember: checkProp(isOperatorMember),
    operator: checkProp(isOperatorDTO),
    queueAgentProfileDTO: checkProp(isQueueAgentProfileDTO),
    queueLoginDTO: checkProp(isQueueLoginDTO),
};

export default {
    PropTypes,
};
